import { useState, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { Moon, Sun, Sparkles, ChevronDown, LayoutDashboard } from 'lucide-react';
import { UserProfileModal } from './UserProfileModal';
import { useNavigate } from 'react-router-dom';

export function Header() {
    const { theme, setTheme } = useTheme();
    const { currentUser } = useAuth();
    const [isProfileOpen, setIsProfileOpen] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const handleScroll = () => setIsScrolled(window.scrollY > 30);
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    return (
        <>
            <header className={`sticky top-0 z-40 w-full border-b-4 border-border px-4 sm:px-6 py-3 flex items-center justify-between transition-all duration-300 ${isScrolled ? 'bg-background/85 backdrop-blur-lg shadow-[0_4px_20px_rgba(0,0,0,0.08)]' : 'bg-background shadow-[0_4px_0_0_var(--border)]'}`}>
                {/* Logo */}
                <div
                    onClick={() => navigate('/')}
                    className="flex items-center gap-1.5 sm:gap-3 cursor-pointer group min-w-0 flex-shrink"
                >
                    <div className="w-7 h-7 sm:w-10 sm:h-10 neo-border bg-accent flex items-center justify-center font-black text-sm sm:text-xl neo-shadow -rotate-6 group-hover:rotate-0 transition-transform duration-300 relative overflow-hidden flex-shrink-0">
                        <span className="relative z-10 text-black">AL</span>
                        <div className="absolute inset-0 bg-primary translate-y-full group-hover:translate-y-0 transition-transform duration-300 z-0"></div>
                        <span className="absolute z-10 text-primary-foreground opacity-0 group-hover:opacity-100 transition-opacity">AL</span>
                    </div>
                    <h1 className="text-lg sm:text-2xl md:text-3xl font-black uppercase tracking-tighter group-hover:text-primary transition-colors flex items-center gap-1.5 truncate">
                        AadishLabs
                        <Sparkles size={16} className="hidden sm:inline-block text-accent fill-accent animate-pulse flex-shrink-0" />
                    </h1>
                </div>

                <div className="flex items-center gap-2 sm:gap-3">
                    {/* Theme Toggle */}
                    <button
                        onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
                        className="p-2 neo-border bg-foreground text-background neo-hover transition-colors relative overflow-hidden"
                        aria-label="Toggle theme"
                    >
                        <div className="relative w-4 h-4 sm:w-5 sm:h-5">
                            <Sun size={20} className={`absolute inset-0 transition-all duration-300 w-full h-full ${theme === 'dark' ? 'rotate-0 scale-100 opacity-100' : 'rotate-90 scale-0 opacity-0'}`} />
                            <Moon size={20} className={`absolute inset-0 transition-all duration-300 w-full h-full ${theme === 'dark' ? '-rotate-90 scale-0 opacity-0' : 'rotate-0 scale-100 opacity-100'}`} />
                        </div>
                    </button>

                    {/* Profile / Login */}
                    {currentUser ? (
                        <div className="flex items-center gap-2 sm:gap-3">
                            <button
                                onClick={() => navigate('/dashboard')}
                                className="hidden sm:flex items-center gap-2 bg-secondary text-secondary-foreground font-black uppercase px-4 py-2 border-4 border-border neo-hover neo-shadow text-xs sm:text-sm h-9 sm:h-12"
                            >
                                <LayoutDashboard size={16} /> Dashboard
                            </button>
                            <button
                                onClick={() => navigate('/dashboard')}
                                className="sm:hidden flex items-center justify-center bg-secondary text-secondary-foreground font-black uppercase w-9 h-9 border-4 border-border neo-hover neo-shadow"
                            >
                                <LayoutDashboard size={16} />
                            </button>

                            <button
                                onClick={() => setIsProfileOpen(true)}
                                className="flex items-center bg-card text-card-foreground hover:translate-y-[2px] hover:translate-x-[2px] transition-transform border-4 border-border rounded-none h-9 sm:h-12 neo-shadow flex-shrink-0"
                            >
                                <div className="h-full w-8 sm:w-12 bg-secondary flex items-center justify-center border-r-4 border-border">
                                    {currentUser.photoURL ? (
                                        <img src={currentUser.photoURL} alt="Avatar" className="w-full h-full object-contain p-0.5" />
                                    ) : (
                                        <span className="font-black text-xs sm:text-sm text-secondary-foreground">{(currentUser.displayName || 'U').charAt(0)}</span>
                                    )}
                                </div>
                                <div className="px-1.5 sm:px-3 font-black uppercase tracking-widest flex items-center gap-1 sm:gap-1.5 text-[10px] sm:text-sm">
                                    <span className="hidden sm:inline max-w-[80px] truncate">{currentUser.displayName || 'User'}</span>
                                    <ChevronDown size={14} strokeWidth={4} className="sm:w-4 sm:h-4" />
                                </div>
                            </button>
                        </div>
                    ) : (
                        <button
                            onClick={() => navigate('/login')}
                            className="bg-accent text-accent-foreground font-black uppercase px-4 sm:px-6 py-2 border-4 border-border neo-hover neo-active text-xs sm:text-sm neo-shadow"
                        >
                            Login
                        </button>
                    )}
                </div>
            </header>

            {isProfileOpen && <UserProfileModal onClose={() => setIsProfileOpen(false)} />}
        </>
    );
}
